import { colors, white } from '@kmns-internal/furikake/tokens'
import {
  Alert,
  AlertTitle,
  Box,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material'
import { FC } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage'
import { Header } from 'src/components/header/Header'
import { HeaderBackButtonMui } from 'src/components/header/HeaderBackButtonMui'
import { HeaderTitle } from 'src/components/header/HeaderTitle'
import { useQueryMaster } from '../api/useQueryMaster'
import MasterRecordsTable from '../components/MasterRecordsTable/MasterRecordsTable'
import RecordsEditMenu from '../components/RecordsEditMenu/RecordsEditMenu'
import { useRecordsActions } from '../hooks/useRecordsActions'
import { useRecordsEditMenuState } from '../hooks/useRecordsEditMenuState'
import { MasterFieldDefsEditButton } from './components/MasterFieldDefsEditButton/MasterFieldDefsEditButton'
import MasterLatestBatchJobStatusDrawer from './components/MasterLatestBatchJobStatusDrawer/MasterLatestBatchJobStatusDrawer'
import MasterRecordsAddUpdateDrawer from './components/MasterRecordsAddUpdateDrawer/MasterRecordsAddUpdateDrawer'
import { MasterRecordsDeleteDrawer } from './components/MasterRecordsDeleteDrawer/MasterRecordsDeleteDrawer'

const MasterDetail: FC = () => {
  const { masterUuid } = useParams<{ masterUuid: string }>()
  const history = useHistory()

  const { data, isLoading, isError } = useQueryMaster(masterUuid)

  const {
    anchorEl,
    isOpen: isOpenEditMenu,
    handleClick: handleClickEditMenu,
    handleClose: handleCloseEditMenu,
  } = useRecordsEditMenuState()
  const { menuItems, drawers } = useRecordsActions(handleCloseEditMenu)

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (isError) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <ErrorMessage noMargin>エラーが発生しました</ErrorMessage>
      </Box>
    )
  }

  if (!data) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <Typography>マスタが見つかりません</Typography>
      </Box>
    )
  }

  return (
    <Stack sx={{ height: '100vh' }}>
      <Box
        sx={{
          height: 72,
          width: '100%',
          alignItems: 'center',
          boxSizing: 'border-box',
          display: 'block',
          flexDirection: 'row',
        }}
      >
        <Header>
          <Stack direction="row" alignItems="center" spacing={1}>
            <HeaderBackButtonMui
              onClick={() => history.push('/masters')}
              data-testid="master-detail-back-button"
            />
            <HeaderTitle title={data.name} />
          </Stack>
        </Header>
      </Box>

      <Alert
        severity="info"
        variant="outlined"
        sx={{
          mx: 3,
          mt: 2,
          '& .MuiAlert-icon': {
            fontSize: '22px',
          },
        }}
      >
        <AlertTitle
          sx={{
            fontWeight: 700,
            fontSize: '16px',
            color: colors.alertInfo.color,
          }}
        >
          値なしの扱いについて
        </AlertTitle>
        <Typography
          variant="body2"
          sx={{
            color: colors.alertInfo.color,
          }}
        >
          数値・テキストには「値なし」を設定できます。製品によって回答不要とする場合は値なしを設定してください。
        </Typography>
        {/* TODO: ヘルプページが作成されたら表示する
            対応チケット: https://kaminashi.atlassian.net/browse/INE-2729
        */}
        {/* <Typography
          variant="body2"
          sx={{
            color: colors.alertInfo.color,
          }}
        >
          詳しい使い方は
          <Link href="" target="_blank" rel="noopener noreferrer">
            ヘルプページ
          </Link>
          をご確認ください。
        </Typography> */}
      </Alert>

      <Box
        width="100%"
        display="flex"
        justifyContent="flex-end"
        sx={{ py: 2, px: 3, gap: 2 }}
      >
        <RecordsEditMenu
          anchorEl={anchorEl}
          isOpen={isOpenEditMenu}
          menuItems={menuItems}
          onMenuClick={handleClickEditMenu}
          onMenuClose={handleCloseEditMenu}
        />
        <MasterFieldDefsEditButton masterUuid={masterUuid} />
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          overflow: 'hidden',
          position: 'relative',
          bgcolor: white,
        }}
      >
        <MasterRecordsTable masterUuid={masterUuid} />
      </Box>

      <MasterRecordsAddUpdateDrawer
        masterUuid={masterUuid}
        masterName={data.name}
        open={drawers.bulkAddUpdate.isOpen}
        closeDrawer={drawers.bulkAddUpdate.close}
      />
      <MasterRecordsDeleteDrawer
        masterUuid={masterUuid}
        masterName={data.name}
        open={drawers.bulkDelete.isOpen}
        closeDrawer={drawers.bulkDelete.close}
      />
      <MasterLatestBatchJobStatusDrawer
        masterUuid={masterUuid}
        open={drawers.latestBatchJobStatus.isOpen}
        closeDrawer={drawers.latestBatchJobStatus.close}
      />
    </Stack>
  )
}

export default MasterDetail
